import React from "react";
import { createRoot } from "react-dom/client";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import GoodsCard from "./GoodsCard";
import { useWidgetProps } from "../use-widget-props";

function App() {
  // useWidgetProps로 toolOutput 데이터 읽기 (fallback 데이터 포함)
  const widgetProps = useWidgetProps(() => ({
    goodsList: [],
    qicResult: null,
  }));

  const goodsList = widgetProps?.goodsList || [];
  const qicResult = widgetProps?.qicResult || null;
  const [isLoading, setIsLoading] = React.useState(true);

  // // 디버깅용 로그
  // React.useEffect(() => {
  //   console.log("widgetProps:", widgetProps);
  //   console.log("qicResult:", qicResult);
  //   console.log("goodsList:", goodsList);
  // }, [widgetProps, qicResult, goodsList]);

  React.useEffect(() => {
    // goodsList가 로드되면 로딩 종료
    if (goodsList && goodsList.length > 0) {
      setIsLoading(false);
    }

    // 5초 후에도 데이터가 없으면 로딩 종료 (타임아웃)
    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 5000);

    return () => clearTimeout(timeout);
  }, [goodsList]);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "center",
    loop: false,
    containScroll: "trimSnaps",
    slidesToScroll: "auto",
    dragFree: false,
  });
  const [canPrev, setCanPrev] = React.useState(false);
  const [canNext, setCanNext] = React.useState(false);

  React.useEffect(() => {
    if (!emblaApi) return;
    const updateButtons = () => {
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
    };
    updateButtons();
    emblaApi.on("select", updateButtons);
    emblaApi.on("reInit", updateButtons);
    return () => {
      emblaApi.off("select", updateButtons);
      emblaApi.off("reInit", updateButtons);
    };
  }, [emblaApi]);

  // if (!goodsList || goodsList.length === 0) {
  //   return (
  //     <div className="antialiased w-full text-black py-5 bg-white">
  //       <div className="text-center text-gray-500 py-10">상품이 없습니다.</div>
  //     </div>
  //   );
  // }

  // 로딩 중일 때
  if (isLoading) {
    return (
      <div className="antialiased w-full text-black dark:text-white py-10 bg-white dark:bg-gray-900">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            상품을 불러오는 중...
          </div>
        </div>
      </div>
    );
  }

  // qicResult가 있고, 최소 하나의 값이라도 비어있지 않은지 확인
  const hasQicData =
    qicResult &&
    ((qicResult.category && qicResult.category.trim()) ||
      (qicResult.brand_name && qicResult.brand_name.trim()) ||
      (qicResult.maker_name && qicResult.maker_name.trim()));

  return (
    <div className="antialiased relative w-full text-black dark:text-white py-5 bg-white dark:bg-gray-900">
      {/* QIC Result 표시 (있을 경우) */}
      {hasQicData && (
        <div className="mb-4 px-5">
          <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                  검색 분석 결과
                </h3>
                <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  {qicResult.category && qicResult.category.trim() && (
                    <div>
                      <span className="font-medium">카테고리:</span>{" "}
                      {qicResult.category}
                    </div>
                  )}
                  {qicResult.brand_name && qicResult.brand_name.trim() && (
                    <div>
                      <span className="font-medium">브랜드:</span>{" "}
                      {qicResult.brand_name}
                    </div>
                  )}
                  {qicResult.maker_name && qicResult.maker_name.trim() && (
                    <div>
                      <span className="font-medium">제조사:</span>{" "}
                      {qicResult.maker_name}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-4 px-5 items-stretch">
          {goodsList.map((goods, index) => (
            <GoodsCard key={index} goods={goods} />
          ))}
        </div>
      </div>
      {/* Edge gradients */}
      <div
        aria-hidden
        className={
          "pointer-events-none absolute inset-y-0 left-0 w-8 z-[5] transition-opacity duration-200 " +
          (canPrev ? "opacity-100" : "opacity-0")
        }
      >
        <div
          className="h-full w-full border-l border-black/15 dark:border-white/15 bg-gradient-to-r from-white dark:from-gray-900 via-white/50 dark:via-gray-900/50 to-transparent"
          style={{
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 0%, white 30%, white 70%, transparent 100%)",
            maskImage:
              "linear-gradient(to bottom, transparent 0%, white 30%, white 70%, transparent 100%)",
          }}
        />
      </div>
      <div
        aria-hidden
        className={
          "pointer-events-none absolute inset-y-0 right-0 w-8 z-[5] transition-opacity duration-200 " +
          (canNext ? "opacity-100" : "opacity-0")
        }
      >
        <div
          className="h-full w-full border-r border-black/15 dark:border-white/15 bg-gradient-to-l from-white dark:from-gray-900 via-white/50 dark:via-gray-900/50 to-transparent"
          style={{
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 0%, white 30%, white 70%, transparent 100%)",
            maskImage:
              "linear-gradient(to bottom, transparent 0%, white 30%, white 70%, transparent 100%)",
          }}
        />
      </div>
      {canPrev && (
        <button
          aria-label="이전"
          className="absolute left-2 top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center h-10 w-10 rounded-full bg-white dark:bg-gray-800 text-black dark:text-white shadow-lg ring ring-black/5 dark:ring-white/5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          onClick={() => emblaApi && emblaApi.scrollPrev()}
          type="button"
        >
          <ArrowLeft strokeWidth={1.5} className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
      {canNext && (
        <button
          aria-label="다음"
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center h-10 w-10 rounded-full bg-white dark:bg-gray-800 text-black dark:text-white shadow-lg ring ring-black/5 dark:ring-white/5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          onClick={() => emblaApi && emblaApi.scrollNext()}
          type="button"
        >
          <ArrowRight
            strokeWidth={1.5}
            className="h-5 w-5"
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  );
}

createRoot(document.getElementById("goods-carousel-root")).render(<App />);
